import { useEffect, useState } from "react";
import IntroScreen from "./Screens/IntroScreen/IntroScreen";
import QuestionScreen from "./Screens/QuestionScreen/QuestionScreen";
import { SelectedAnswer } from "./Screens/QuestionScreen/QuestionScreen";
import { postBloomreachPreAnalysisLead } from "../../utils/api";
import { BloomreachPreAnalysisLeadModel } from "../../types/BloomreachPreAnalysisLeadModel";
import { getCookie } from "../../utils/cookieService";
import Analytics from "../../utils/analytics";
import { FadeInWrapper, IntroLayer, TransitionRoot } from "./BehaviourAnalysis.style";

export enum types {
  tryghed = "tryghed",
  struktur = "struktur",
  relation = "relation",
  balance = "balance",
  fremdrift = "fremdrift",
}

type BehaviourResult = {
  dominantType: types;
  countsByType: Record<types, number>;
  percentagesByType: Record<types, number>;
};

export const questions = [
  {
    id: 1,
    question: "Når du står overfor en vigtig beslutning, hvad gør du typisk først?",
    order: 1,
    answers: [
      { id: "a1", answer: "Jeg undersøger, hvilke konsekvenser de forskellige muligheder kan få.", value: types.tryghed },
      { id: "a2", answer: "Jeg samler information og forsøger at få et klart overblik.", value: types.struktur },
      { id: "a3", answer: "Jeg vender mine tanker med andre for at få flere perspektiver.", value: types.relation },
      { id: "a4", answer: "Jeg ser på, hvordan beslutningen påvirker situationen som helhed.", value: types.balance },
      { id: "a5", answer: "Jeg fokuserer på, hvilken løsning der bringer os hurtigst videre.", value: types.fremdrift },
    ],
  },
  {
    id: 2,
    question: "Hvad vægter du højest, når du skal vælge mellem flere muligheder?",
    order: 2,
    answers: [
      { id: "b1", answer: "At det virker robust på længere sigt.", value: types.tryghed },
      { id: "b2", answer: "At der er en tydelig plan for, hvordan den skal gennemføres.", value: types.struktur },
      { id: "b3", answer: "At de involverede personer kan se sig selv i beslutningen.", value: types.relation },
      { id: "b4", answer: "At forskellige hensyn bliver afvejet på en fornuftig måde.", value: types.balance },
      { id: "b5", answer: "At det skaber synlige resultater.", value: types.fremdrift },
    ],
  },
  {
    id: 3,
    question: "Hvis noget er uklart, hvad gør du typisk?",
    order: 3,
    answers: [
      { id: "c1", answer: "Jeg afklarer, hvad der kan gå galt, før jeg fortsætter.", value: types.tryghed },
      { id: "c2", answer: "Jeg søger efter information eller konkrete retningslinjer.", value: types.struktur },
      { id: "c3", answer: "Jeg taler med relevante personer for at få deres input.", value: types.relation },
      { id: "c4", answer: "Jeg forsøger at skabe et samlet billede af situationen.", value: types.balance },
      { id: "c5", answer: "Jeg tager de første skridt og justerer undervejs.", value: types.fremdrift },
    ],
  },
  {
    id: 4,
    question: "Når du har truffet en beslutning, hvornår føles den rigtig?",
    order: 4,
    answers: [
      { id: "d1", answer: "Når jeg har tænkt de mulige udfordringer igennem.", value: types.tryghed },
      { id: "d2", answer: "Når beslutningen bygger på et solidt grundlag.", value: types.struktur },
      { id: "d3", answer: "Når alle er blevet hørt.", value: types.relation },
      { id: "d4", answer: "Når beslutningen tager højde for flere perspektiver.", value: types.balance },
      { id: "d5", answer: "Når den omsættes til konkret handling.", value: types.fremdrift },
    ],
  },
  {
    id: 5,
    question: "Hvilken type feedback oplever du som mest værdifuld?",
    order: 5,
    answers: [
      { id: "e1", answer: "Feedback der hjælper mig med at undgå fejl.", value: types.tryghed },
      { id: "e2", answer: "Feedback der giver mig klarhed og retning.", value: types.struktur },
      { id: "e3", answer: "Feedback der bygger på andres erfaringer.", value: types.relation },
      { id: "e4", answer: "Feedback der hjælper mig med at se flere sider af sagen.", value: types.balance },
      { id: "e5", answer: "Feedback der peger på næste skridt.", value: types.fremdrift },
    ],
  },
  {
    id: 6,
    question: "Hvis du er i tvivl om, hvordan noget skal løses, hvad gør du typisk?",
    order: 6,
    answers: [
      { id: "f1", answer: "Jeg undersøger tidligere erfaringer for at minimere risikoen for fejl.", value: types.tryghed },
      { id: "f2", answer: "Jeg leder efter dokumentation.", value: types.struktur },
      { id: "f3", answer: "Jeg spørger nogen med relevant erfaring.", value: types.relation },
      { id: "f4", answer: "Jeg vurderer, hvad der giver bedst mening i den konkrete situation.", value: types.balance },
      { id: "f5", answer: "Jeg vælger en retning og tilpasser undervejs.", value: types.fremdrift },
    ],
  },
  {
    id: 7,
    question: "Hvad får dig hurtigst til at have tillid til en ny person?",
    order: 7,
    answers: [
      { id: "g1", answer: "At personen virker stabil og pålidelig.", value: types.tryghed },
      { id: "g2", answer: "At personen kommunikerer klart og præcist.", value: types.struktur },
      { id: "g3", answer: "At personen er imødekommende og oprigtigt interesseret i andre.", value: types.relation },
      { id: "g4", answer: "At personen viser forståelse for forskellige perspektiver.", value: types.balance },
      { id: "g5", answer: "At personen tager ansvar og får ting til at ske.", value: types.fremdrift },
    ],
  },
  {
    id: 8,
    question: "Hvad kendetegner de dage, hvor du er mest tilfreds?",
    order: 8,
    answers: [
      { id: "h1", answer: "Når de er forløbet uden større overraskelser.", value: types.tryghed },
      { id: "h2", answer: "Når jeg har skabt orden og overblik.", value: types.struktur },
      { id: "h3", answer: "Når jeg har haft gode oplevelser med andre.", value: types.relation },
      { id: "h4", answer: "Når jeg har fået forskellige hensyn til at fungere sammen.", value: types.balance },
      { id: "h5", answer: "Når jeg kan se tydelige fremskridt eller resultater.", value: types.fremdrift },
    ],
  },
  {
    id: 9,
    question: "Hvad hjælper dig mest, når du oplever pres?",
    order: 9,
    answers: [
      { id: "i1", answer: "At vide, hvad jeg kan regne med.", value: types.tryghed },
      { id: "i2", answer: "At få styr på processen.", value: types.struktur },
      { id: "i3", answer: "At sparre med andre.", value: types.relation },
      { id: "i4", answer: "At prioritere og skabe overblik.", value: types.balance },
      { id: "i5", answer: "At tage fat på det vigtigste med det samme.", value: types.fremdrift },
    ],
  },
  {
    id: 10,
    question: "Hvordan støtter andre dig bedst, når du står over for en udfordring?",
    order: 10,
    answers: [
      { id: "j1", answer: "Ved at skabe ro omkring situationen.", value: types.tryghed },
      { id: "j2", answer: "Ved at hjælpe med at danne et overblik.", value: types.struktur },
      { id: "j3", answer: "Ved at lytte og dele deres perspektiv.", value: types.relation },
      { id: "j4", answer: "Ved at hjælpe med at afveje forskellige hensyn.", value: types.balance },
      { id: "j5", answer: "Ved at hjælpe mig med at komme videre.", value: types.fremdrift },
    ],
  },
  {
    id: 11,
    question: "Når der sker større forandringer, hvad er din første reaktion?",
    order: 11,
    answers: [
      { id: "k1", answer: "Jeg overvejer, hvilke konsekvenser de kan få.", value: types.tryghed },
      { id: "k2", answer: "Jeg forsøger at forstå planen bag ændringerne.", value: types.struktur },
      { id: "k3", answer: "Jeg tænker på, hvordan de påvirker andre omkring mig.", value: types.relation },
      { id: "k4", answer: "Jeg prøver at se både muligheder og udfordringer.", value: types.balance },
      { id: "k5", answer: "Jeg bliver nysgerrig på, hvad de kan føre til.", value: types.fremdrift },
    ],
  },
  {
    id: 12,
    question: "Hvad giver dig mest energi i hverdagen?",
    order: 12,
    answers: [
      { id: "l1", answer: "At skabe noget holdbart og stabilt.", value: types.tryghed },
      { id: "l2", answer: "At skabe orden og sammenhæng.", value: types.struktur },
      { id: "l3", answer: "At bidrage til gode relationer og fællesskab.", value: types.relation },
      { id: "l4", answer: "At finde løsninger, der fungerer for alle.", value: types.balance },
      { id: "l5", answer: "At nå mål og skabe resultater.", value: types.fremdrift },
    ],
  },
  {
    id: 13,
    question: "Hvilken beskrivelse passer bedst på dig?",
    order: 13,
    answers: [
      { id: "m1", answer: "Jeg tænker grundigt over konsekvenserne af mine valg.", value: types.tryghed },
      { id: "m2", answer: "Jeg arbejder bedst med systematik og overblik.", value: types.struktur },
      { id: "m3", answer: "Jeg interesserer mig naturligt for mennesker.", value: types.relation },
      { id: "m4", answer: "Jeg ser ofte flere sider af samme sag.", value: types.balance },
      { id: "m5", answer: "Jeg fokuserer på at skabe fremdrift.", value: types.fremdrift },
    ],
  },
  {
    id: 14,
    question: "Når noget er særligt vigtigt, hvordan griber du det an?",
    order: 14,
    answers: [
      { id: "n1", answer: "Jeg sikrer mig, at fundamentet er på plads.", value: types.tryghed },
      { id: "n2", answer: "Jeg laver en plan for processen.", value: types.struktur },
      { id: "n3", answer: "Jeg inddrager relevante personer tidligt.", value: types.relation },
      { id: "n4", answer: "Jeg undersøger, hvordan forskellige faktorer hænger sammen.", value: types.balance },
      { id: "n5", answer: "Jeg omsætter hurtigt idéer til handling.", value: types.fremdrift },
    ],
  },
  {
    id: 15,
    question: "Når du starter på noget nyt, hvad gør du typisk først?",
    order: 15,
    answers: [
      { id: "o1", answer: "Jeg afklarer, hvad der forventes.", value: types.tryghed },
      { id: "o2", answer: "Jeg organiserer arbejdet i overskuelige trin.", value: types.struktur },
      { id: "o3", answer: "Jeg skaber kontakt til de personer, der er involveret.", value: types.relation },
      { id: "o4", answer: "Jeg prøver at forstå helheden.", value: types.balance },
      { id: "o5", answer: "Jeg tager hul på det konkrete først.", value: types.fremdrift },
    ],
  },
  {
    id: 16,
    question: "Hvad kan frustrere dig mest i hverdagen?",
    order: 16,
    answers: [
      { id: "p1", answer: "Når ting ændrer sig uden varsel.", value: types.tryghed },
      { id: "p2", answer: "Når der mangler struktur eller retning.", value: types.struktur },
      { id: "p3", answer: "Når samarbejdet ikke fungerer.", value: types.relation },
      { id: "p4", answer: "Når vigtige hensyn bliver overset.", value: types.balance },
      { id: "p5", answer: "Når tingene står stille for længe.", value: types.fremdrift },
    ],
  },
  {
    id: 17,
    question: "Når du møder et problem, hvad er din naturlige tilgang?",
    order: 17,
    answers: [
      { id: "q1", answer: "Jeg vurderer først de mulige risici.", value: types.tryghed },
      { id: "q2", answer: "Jeg analyserer årsagerne systematisk.", value: types.struktur },
      { id: "q3", answer: "Jeg søger input fra andre.", value: types.relation },
      { id: "q4", answer: "Jeg ser på problemet fra flere vinkler.", value: types.balance },
      { id: "q5", answer: "Jeg afprøver hurtigt mulige løsninger.", value: types.fremdrift },
    ],
  },
  {
    id: 18,
    question: "Hvilken rolle får du ofte i en gruppe?",
    order: 18,
    answers: [
      { id: "r1", answer: "Den der skaber stabilitet og kontinuitet.", value: types.tryghed },
      { id: "r2", answer: "Den der holder styr på processer og detaljer.", value: types.struktur },
      { id: "r3", answer: "Den der samler alle og skaber dialog.", value: types.relation },
      { id: "r4", answer: "Den der hjælper med at skabe fælles forståelse.", value: types.balance },
      { id: "r5", answer: "Den der driver arbejdet fremad.", value: types.fremdrift },
    ],
  },
  {
    id: 19,
    question: "Hvornår trives du bedst?",
    order: 19,
    answers: [
      { id: "s1", answer: "Når rammerne er tydelige og forudsigelige.", value: types.tryghed },
      { id: "s2", answer: "Når der er et overblik og et mål.", value: types.struktur },
      { id: "s3", answer: "Når relationerne fungerer godt.", value: types.relation },
      { id: "s4", answer: "Når alle har det godt.", value: types.balance },
      { id: "s5", answer: "Når der er energi og udvikling.", value: types.fremdrift },
    ],
  },
  {
    id: 20,
    question: "Hvordan lærer du bedst noget nyt?",
    order: 20,
    answers: [
      { id: "t1", answer: "Når jeg får tid og ro til at forstå det i mit eget tempo.", value: types.tryghed },
      { id: "t2", answer: "Når læringen er bygget logisk op og nem at følge.", value: types.struktur },
      { id: "t3", answer: "Når jeg kan tale med andre om det og dele erfaringer.", value: types.relation },
      { id: "t4", answer: "Når jeg forstår helheden og hvorfor det er vigtigt.", value: types.balance },
      { id: "t5", answer: "Når jeg får lov til selv at afprøve det i praksis.", value: types.fremdrift },
    ],
  },
  {
    id: 21,
    question: "Hvilken egenskab værdsætter du mest hos en person, der tager ansvar?",
    order: 21,
    answers: [
      { id: "u1", answer: "Evnen til at skabe trygge rammer.", value: types.tryghed },
      { id: "u2", answer: "Evnen til at skabe en klar retning.", value: types.struktur },
      { id: "u3", answer: "Evnen til at inkludere og involvere andre.", value: types.relation },
      { id: "u4", answer: "Evnen til at tage hensyn til forskellige behov.", value: types.balance },
      { id: "u5", answer: "Evnen til at få ting til at ske.", value: types.fremdrift },
    ],
  },
  {
    id: 22,
    question: "Når noget skal nås til tiden, hvad gør du typisk?",
    order: 22,
    answers: [
      { id: "v1", answer: "Jeg sikrer, at kvaliteten er i orden hele vejen.", value: types.tryghed },
      { id: "v2", answer: "Jeg holder mig til planen og prioriterer det vigtigste.", value: types.struktur },
      { id: "v3", answer: "Jeg afstemmer med de andre, der er involveret.", value: types.relation },
      { id: "v4", answer: "Jeg holder fokus på helheden og det, der betyder mest.", value: types.balance },
      { id: "v5", answer: "Jeg koncentrerer mig om at nå i mål.", value: types.fremdrift },
    ],
  },
  {
    id: 23,
    question: "Hvad kendetegner et godt miljø for dig?",
    order: 23,
    answers: [
      { id: "w1", answer: "At der er gensidig tillid.", value: types.tryghed },
      { id: "w2", answer: "At roller og forventninger er tydelige.", value: types.struktur },
      { id: "w3", answer: "At man hjælper og støtter hinanden.", value: types.relation },
      { id: "w4", answer: "At der er plads til forskellige meninger.", value: types.balance },
      { id: "w5", answer: "At der er energi og udvikling.", value: types.fremdrift },
    ],
  },
  {
    id: 24,
    question: "Hvornår mister du typisk motivation?",
    order: 24,
    answers: [
      { id: "x1", answer: "Når der er for meget usikkerhed.", value: types.tryghed },
      { id: "x2", answer: "Når der mangler struktur og overblik.", value: types.struktur },
      { id: "x3", answer: "Når relationer ikke fungerer.", value: types.relation },
      { id: "x4", answer: "Når beslutninger virker ensidige.", value: types.balance },
      { id: "x5", answer: "Når udviklingen går i stå.", value: types.fremdrift },
    ],
  },
  {
    id: 25,
    question: "Når noget lykkes, hvad giver dig størst tilfredshed?",
    order: 25,
    answers: [
      { id: "y1", answer: "At det er bygget på et solidt fundament.", value: types.tryghed },
      { id: "y2", answer: "At det er gennemført på en god måde.", value: types.struktur },
      { id: "y3", answer: "At det er skabt sammen med andre.", value: types.relation },
      { id: "y4", answer: "At det fungerer godt på tværs af behov og interesser.", value: types.balance },
      { id: "y5", answer: "At det har skabt synlige resultater.", value: types.fremdrift },
    ],
  },
];

const pages = [
  { id: 1, questionId: 1 },
  { id: 2, questionId: 2 },
  { id: 3, questionId: 3 },
  { id: 4, questionId: 4 },
  { id: 5, questionId: 5 },
  { id: 6, questionId: 6 },
  { id: 7, questionId: 7 },
  { id: 8, questionId: 8 },
  { id: 9, questionId: 9 },
  { id: 10, questionId: 10 },
  { id: 11, questionId: 11 },
  { id: 12, questionId: 12 },
  { id: 13, questionId: 13 },
  { id: 14, questionId: 14 },
  { id: 15, questionId: 15 },
  { id: 16, questionId: 16 },
  { id: 17, questionId: 17 },
  { id: 18, questionId: 18 },
  { id: 19, questionId: 19 },
  { id: 20, questionId: 20 },
  { id: 21, questionId: 21 },
  { id: 22, questionId: 22 },
  { id: 23, questionId: 23 },
  { id: 24, questionId: 24 },
  { id: 25, questionId: 25 },
];

export default function BehaviourAnalysis() {
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [situationUrl, setSituationUrl] = useState<string | null>(null);
  const [showIntro, setShowIntro] = useState(true);
  const [showQuestion, setShowQuestion] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const [result, setResult] = useState<SelectedAnswer[] | null>(null);
  //const [resultType, setResultType] = useState<types | null>(null);
  const [countByType, setCountByType] = useState<Record<types, number> | null>(null);
  const [percentagesByType, setPercentagesByType] = useState<Record<types, number> | null>(null);
  const [cookieAnswered, setCookieAnswered] = useState(false);
  const cookie = getCookie("__exponea_etc__");

  //Check if cookuehub has been answered
  useEffect(() => {
    if ((window as any).cookiehub?.hasAnswered()) {
      setCookieAnswered(true);
      return;
    }
    const interval = setInterval(() => {
      if ((window as any).cookiehub?.hasAnswered()) {
        setCookieAnswered(true);
        clearInterval(interval);
      }
    }, 200);
    return () => clearInterval(interval);
  }, []);

  //Get data-result-url
  useEffect(() => {
    const fetchData = async () => {
      try {
        const resultUrl = form?.dataset.resultUrl;
        if (resultUrl) {
          setResultUrl(resultUrl);
        }
      } catch (err) {}
    };

    const form = document.querySelector(`[data-component='behaviour-analysis']`) as HTMLElement;

    fetchData();
  }, []);

  //Get url params for situation
  useEffect(() => {
    const queryString = window.location.search;
    const urlParams = new URLSearchParams(queryString);
    const situation = urlParams.get("situation");

    if (situation) {
      setSituationUrl(situation);
    } else {
      setSituationUrl("salary");
    }
  }, []);

  //Timer for showing intro screen - starts when cookie overlay is dismissed
  useEffect(() => {
    //If there are url params, skip intro and go directly to questions
    const hasUrlParams = new URLSearchParams(window.location.search).toString().length > 0;

    if (hasUrlParams) {
      setShowQuestion(true);
      setShowIntro(false);
      return;
    }

    if (!cookieAnswered) return;

    const fadeOutTimer = window.setTimeout(() => {
      setShowQuestion(true);
      setIsFadingOut(true);
    }, 5500);

    const introTimer = window.setTimeout(() => {
      setShowQuestion(true);
      setShowIntro(false);
      setIsFadingOut(false);
      Analytics.getInstance().trackBehaviourProfiling("behaviour_start", "behavioral", "start");
    }, 6000);

    return () => {
      window.clearTimeout(fadeOutTimer);
      window.clearTimeout(introTimer);
    };
  }, [cookieAnswered]);

  //Result calculation
  const calculateBehaviourResult = (answers: SelectedAnswer[]) => {
    setResult(answers);

    const allTypes = Object.values(types);
    const countsByType = allTypes.reduce(
      (acc, type) => {
        acc[type] = 0;
        return acc;
      },
      {} as Record<types, number>,
    );

    answers.forEach((answer) => {
      countsByType[answer.type] += 1;
    });

    const typeTiebreakerPriority: types[] = [types.balance, types.tryghed, types.struktur, types.relation, types.fremdrift];

    const dominantType = allTypes.reduce((currentMaxType, nextType) => {
      const countDiff = countsByType[nextType] - countsByType[currentMaxType];

      if (countDiff !== 0) {
        return countDiff > 0 ? nextType : currentMaxType;
      }

      const nextTypePriority = typeTiebreakerPriority.indexOf(nextType);
      const currentMaxTypePriority = typeTiebreakerPriority.indexOf(currentMaxType);

      return nextTypePriority < currentMaxTypePriority ? nextType : currentMaxType;
    }, allTypes[0]);

    const totalAnswers = questions.length;
    const calculatedPercentagesByType = allTypes.reduce(
      (acc, type) => {
        acc[type] = totalAnswers > 0 ? Number(((countsByType[type] / totalAnswers) * 100).toFixed(2)) : 0;
        return acc;
      },
      {} as Record<types, number>,
    );

    //setResultType(dominantType);
    setCountByType(countsByType);
    setPercentagesByType(calculatedPercentagesByType);

    // tracking result
    Analytics.getInstance().trackBehaviourProfiling("behaviour_result", "behavioral", dominantType);
    //console.log("%type", calculatedPercentagesByType);
    Object.entries(calculatedPercentagesByType).forEach(([type, percentage]) => {
      //console.log("tracking", type, percentage);
      Analytics.getInstance().trackBehaviourProfiling("behavioral_procents", "behavioral", `${type}_${percentage}`);
    });

    sendResult(dominantType, calculatedPercentagesByType);
  };

  //Handle sending result
  const sendResult = async (dominantType: types, calculatedPercentagesByType: Record<types, number>) => {
    if (resultUrl) {
      if (cookie) {
        // cookie check for pre-analysis lead submission, if cookie exists submit pre-analysis lead and then redirect to result page, if not just redirect to result page
        let lead = {} as BloomreachPreAnalysisLeadModel;
        lead.campaign = "Adfærdsanalyse";
        lead.cookie = cookie;
        lead.situation = situationUrl || "salary";
        lead.profiletype = dominantType;
        lead.safetyScore = calculatedPercentagesByType.tryghed;
        lead.structureScore = calculatedPercentagesByType.struktur;
        lead.balanceScore = calculatedPercentagesByType.balance;
        lead.relationScore = calculatedPercentagesByType.relation;
        lead.firstmoverScore = calculatedPercentagesByType.fremdrift;
        await postBloomreachPreAnalysisLead(lead).then(() => {
          let resultUrlWithParams = `${resultUrl}/?situation=${situationUrl}&resultType=${dominantType}`;
          window.location.href = resultUrlWithParams;
          Analytics.getInstance().trackBehaviourProfilingCta(dominantType, resultUrlWithParams, "Se resultat");
        });
      } else {
        let resultUrlWithParams = `${resultUrl}/?situation=${situationUrl}&resultType=${dominantType}`;
        window.location.href = resultUrlWithParams;
        Analytics.getInstance().trackBehaviourProfilingCta(dominantType, resultUrlWithParams, "Se resultat");
      }
    }
  };

  return (
    <TransitionRoot>
      {showQuestion && (
        <FadeInWrapper>
          <QuestionScreen questions={questions} pages={pages} onResult={(answers) => calculateBehaviourResult(answers)} />
        </FadeInWrapper>
      )}
      {showIntro && (
        <IntroLayer isOverlay={showQuestion}>
          <IntroScreen isExiting={isFadingOut} cookieAnswered={cookieAnswered} />
        </IntroLayer>
      )}
    </TransitionRoot>
  );
}
